import {colorSwatch} from '../utils/colorSwatch.js';
import {MenuPanels} from '../../models/UIModel.js';
import React, {useState, useMemo, useEffect, useContext, useCallback} from 'react';
import {formatString, insertNewlines, parseColor, setAlpha} from '../utils/utils.js';
import {MenuButton} from '../components/menu-button.js';
import {
    Column,
    Container,
    MaterialContext,
    Row,
    Text,
} from '@wonderlandengine/react-ui/components';
import {
    Align,
    FlexDirection,
    Justify,
    PositionType,
    YogaNodeProps,
} from '@wonderlandengine/react-ui';
import {goToMenu} from '../utils/goToMenu.js';
import {
    RotBMaterialContextValue,
    useRotBMaterialContext,
} from '../utils/rotb-material-context.js';
import {Texture} from '@wonderlandengine/api';
import {WindowHeader} from '../components/window-header.js';

import {UiTexturesManager} from '../components/ui-textures-manager.js';
import {Localization} from '../../utils/Localization.js';
import {Carousel, CarouselItem} from '../components/carousel.js';
import {SliderItem} from '../components/SliderComponent.js';
import {Window} from '../components/window.js';
import {StatNames} from '../../enums/StatNames.js';
import {MalRoot} from '../components/mal-root.js';
import {Header} from '../components/header.js';
import {BooleanName} from '../../enums/BooleanName.js';
import {BooleanStore} from '../../components/boolean-store.js';
import {GameModeType} from '../../enums/GameModeType.js';
import {ColoredImage} from '../components/colored-image.js';
import {goToMainMenu} from '../utils/gotoMainMenu.js';
import {GameFlowController} from '../../components/game-flow-controller.js';
import {RotatableImage} from '../components/rotatable-image.js';
import {RankManager} from '../../components/rank-manager.js';
import {formatNumber} from '../../utils/Utils.js';
import {UnlockManager} from '../../components/unlock-manager.js';
import {RNG} from '../../utils/rng.js';
import {useMemoMaterial} from '../utils/MaterialPool.js';
import {UIText} from '../../types/materials.js';
import {goToPreviousMenu} from '../utils/goToPreviousMenu.js';
import {Sounds} from '../../enums/Sounds.js';

export function isGameModeLocked(booleanName: BooleanName) {
    const unlocked = BooleanStore.instance.get(booleanName);
    return !unlocked;
}

export function lockImage(booleanName: BooleanName, image: Texture): Texture {
    if (!isGameModeLocked(booleanName)) {
        return image;
    } else return UiTexturesManager.instance.buttonLock;
}

type ModeSelectItem = {
    title: string;
    description: string;
    difficultyLevel: number;
    gamesPlayedStat: StatNames;
    autoPop: boolean;
    unlockedItem: BooleanName;
    lockFlag?: BooleanName;
    ignoreLock: boolean;
    implemented: boolean;
};

const GameModeData = new Map<GameModeType, ModeSelectItem>([
    [
        GameModeType.Quickplay,
        {
            title: 'Quickplay',
            description: 'MalModeSelectDescriptionQuickplay',
            difficultyLevel: 1,
            gamesPlayedStat: StatNames.QuickplayGamesPlayed,
            autoPop: false,
            unlockedItem: BooleanName.QuickplayModeClicked,
            ignoreLock: true,
            implemented: true,
        },
    ],
    [
        GameModeType.Cupcake,
        {
            title: 'Cupcake',
            description: 'MalModeSelectDescriptionCupcake',
            difficultyLevel: 1,
            gamesPlayedStat: StatNames.CupcakeGamesPlayed,
            autoPop: true,
            unlockedItem: BooleanName.CupcakeModeClicked,
            lockFlag: BooleanName.CupcakeModeUnlocked,
            ignoreLock: true,
            implemented: true,
        },
    ],
    [
        GameModeType.Easy,
        {
            title: 'Easy',
            description: 'MalModeSelectDescriptionEasy',
            ignoreLock: true,
            lockFlag: BooleanName.EasyModeUnlocked,
            unlockedItem: BooleanName.EasyModeClicked,
            gamesPlayedStat: StatNames.EasyGamesPlayed,
            autoPop: false,
            difficultyLevel: 2,
            implemented: true,
        },
    ],
    [
        GameModeType.Survival,
        {
            title: 'Skull',
            description: 'MalModeSelectDescriptionSkull',
            ignoreLock: false,
            lockFlag: BooleanName.SurvivalModeUnlocked,
            unlockedItem: BooleanName.SurvivalModeClicked,
            gamesPlayedStat: StatNames.SurvivalGamesPlayed,
            autoPop: false,
            difficultyLevel: 4,
            implemented: false,
        },
    ],
    [
        GameModeType.Normal,
        {
            title: 'Normal',
            description: 'MalModeSelectDescriptionNormal',
            ignoreLock: false,
            lockFlag: BooleanName.NormalModeUnlocked,
            unlockedItem: BooleanName.NormalModeClicked,
            gamesPlayedStat: StatNames.NormalGamesPlayed,
            autoPop: false,
            difficultyLevel: 5,
            implemented: false,
        },
    ],
    [
        GameModeType.Timed,
        {
            title: 'Timed',
            description: 'MalModeSelectDescriptionTimed',
            ignoreLock: false,
            lockFlag: BooleanName.TimedModeUnlocked,
            unlockedItem: BooleanName.TimedModeClicked,
            gamesPlayedStat: StatNames.TimedGamesPlayed,
            autoPop: true,
            difficultyLevel: 5,
            implemented: false,
        },
    ],
    [
        GameModeType.Fungus,
        {
            title: 'Fungus',
            description: 'MalModeSelectDescriptionFungus',
            ignoreLock: false,
            lockFlag: BooleanName.FungusModeUnlocked,
            unlockedItem: BooleanName.FungusModeClicked,
            gamesPlayedStat: StatNames.FungusGamesPlayed,
            autoPop: true,
            difficultyLevel: 3,
            implemented: false,
        },
    ],
]);

type ModeSelectState = {
    title: string;
    description: string;
    diffLevel: number;
    isImplemented: boolean;
    isLocked: boolean;
    isAutoPop: boolean;
    selectedMode: GameModeType;
};

export const ModeSelectPanel = (props: React.PropsWithChildren<YogaNodeProps> & {}) => {
    const [isTextReadyToRender, setIsTextReadyToRender] = useState(false);

    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;
    const mats = useContext(MaterialContext);

    const rank = useMemo(
        () =>
            formatString(
                Localization.instance.get('RankFormat'),
                formatNumber(RankManager.instance.rank)
            ),
        [props]
    );

    const xpPercentage = useMemo(
        () => RankManager.instance.currentRankPercentComplete,
        [props]
    );

    const [modeState, setModeState] = useState<ModeSelectState>({
        title: '',
        description: '',
        diffLevel: 3,
        isImplemented: false,
        isLocked: true,
        isAutoPop: false,
        selectedMode: GameModeType.Quickplay,
    });

    const [selectModeText] = useState(
        Localization.instance.get(
            RNG.getItem(['GameSetupMenuText1', 'GameSetupMenuText2', 'GameSetupMenuText3'])
        )
    );

    const textMaterial = useMemoMaterial('textMaterial', () => {
        return mats.textMaterial.clone() as UIText;
    });

    const descriptionTextMaterial = useMemoMaterial('descriptionTextMaterial_90%', () => {
        const mat = rotbMats.regularTextMaterial.clone() as UIText;
        mat.setColor(parseColor(setAlpha(colorSwatch.DescriptionText, '90%')));
        return mat;
    });

    const onSelectionChanged = useCallback(
        (item: SliderItem) => {
            const currentSelectedMode = GameModeType[item.id];
            const data = GameModeData.get(currentSelectedMode);

            const locked = data.ignoreLock ? false : isGameModeLocked(data.lockFlag!);
            if (
                modeState.isLocked !== locked ||
                modeState.isImplemented !== data.implemented
            ) {
                setIsTextReadyToRender(false);
            }
            setModeState({
                title: Localization.instance.get(data.title),
                description: locked
                    ? UnlockManager.instance.getInstructionsForUnlock(data.lockFlag, 0)
                          .instructions
                    : Localization.instance.get(data.description),
                diffLevel: data.difficultyLevel,
                isImplemented: data.implemented,
                isAutoPop: data.autoPop,
                isLocked: locked,
                selectedMode: currentSelectedMode,
            });
        },
        [modeState.isLocked, modeState.isImplemented]
    );

    useEffect(() => {
        if (!isTextReadyToRender) {
            let frameId = setTimeout(() => {
                setIsTextReadyToRender(true);
            }, 0);
            return () => clearTimeout(frameId);
        }
    }, [isTextReadyToRender]);

    const handleClick = useCallback(() => {
        GameFlowController.instance.setMode(modeState.selectedMode);
        goToMenu(MenuPanels.StageSelect);
    }, [modeState.selectedMode]);

    return (
        <Container
            position={PositionType.Absolute}
            width="100%"
            height="100%"
            flexDirection={FlexDirection.Row}
            justifyContent={Justify.Center}
        >
            <Column marginTop={36}>
                <Window>
                    <Column
                        height="100%"
                        marginTop={20}
                        alignSelf={Align.Center}
                        width={500}
                        rowGap={20}
                    >
                        <WindowHeader title={Localization.instance.get('ModeSelect')} />
                        <Container marginLeft={-20} marginBottom={40}>
                            <MalRoot
                                rank={rank}
                                xpPercentage={xpPercentage}
                                additionalRotation={[-1.5, 1.5, 0]}
                                text={selectModeText}
                            ></MalRoot>
                        </Container>
                        <Container flexGrow={1}>
                            <Container>
                                <Carousel
                                    loop={false}
                                    onSelectionChanged={onSelectionChanged}
                                >
                                    <CarouselItem
                                        title="Cupcake"
                                        id="Cupcake"
                                        image={UiTexturesManager.instance.gameModeCupcake}
                                    ></CarouselItem>
                                    <CarouselItem
                                        title="Star"
                                        id={'Quickplay'}
                                        image={UiTexturesManager.instance.gameModeStar}
                                    ></CarouselItem>
                                    <CarouselItem
                                        title="Normal"
                                        id="Easy"
                                        image={UiTexturesManager.instance.gameModeNormal}
                                    ></CarouselItem>
                                    <CarouselItem
                                        title="Skull"
                                        id="Survival"
                                        image={UiTexturesManager.instance.gameModeSkull}
                                    ></CarouselItem>
                                    <CarouselItem
                                        title="Match"
                                        id="Normal"
                                        image={UiTexturesManager.instance.gameModeMatch}
                                    ></CarouselItem>
                                    <CarouselItem
                                        title="Timed"
                                        id="Timed"
                                        image={UiTexturesManager.instance.gameModeTimed}
                                    ></CarouselItem>
                                    <CarouselItem
                                        title="Mushroom"
                                        id="Fungus"
                                        image={UiTexturesManager.instance.gameModeMushroom}
                                    ></CarouselItem>
                                </Carousel>
                            </Container>
                            <Header
                                title={modeState.title}
                                color={colorSwatch.SubTitleHighBackground}
                                center={true}
                            />
                            <Container>
                                {modeState.isAutoPop && (
                                    <RotatableImage
                                        z={0.01}
                                        position={PositionType.Absolute}
                                        top={-50}
                                        left={-10}
                                        rounding={0}
                                        width={150}
                                        height={75}
                                        rotation={15}
                                        src={UiTexturesManager.instance.autoPop}
                                    ></RotatableImage>
                                )}
                            </Container>
                            <Row margin={20} alignSelf={Align.Center} gap={10}>
                                <ColoredImage
                                    src={UiTexturesManager.instance.diffStar}
                                    width={40}
                                    height={40}
                                    rounding={0}
                                    color="#ffffffff"
                                ></ColoredImage>
                                <ColoredImage
                                    src={UiTexturesManager.instance.diffStar}
                                    width={40}
                                    height={40}
                                    rounding={0}
                                    color={
                                        modeState.diffLevel >= 2 ? '#ffffffff' : '#ffffff80'
                                    }
                                ></ColoredImage>
                                <ColoredImage
                                    src={UiTexturesManager.instance.diffStar}
                                    width={40}
                                    height={40}
                                    rounding={0}
                                    color={
                                        modeState.diffLevel >= 3 ? '#ffffffff' : '#ffffff80'
                                    }
                                ></ColoredImage>
                                <ColoredImage
                                    src={UiTexturesManager.instance.diffStar}
                                    width={40}
                                    height={40}
                                    rounding={0}
                                    color={
                                        modeState.diffLevel >= 4 ? '#ffffffff' : '#ffffff80'
                                    }
                                ></ColoredImage>
                                <ColoredImage
                                    src={UiTexturesManager.instance.diffStar}
                                    width={40}
                                    height={40}
                                    rounding={0}
                                    color={
                                        modeState.diffLevel >= 5 ? '#ffffffff' : '#ffffff80'
                                    }
                                ></ColoredImage>
                            </Row>
                            <Container
                                marginTop={20}
                                height={75}
                                alignContent={Align.FlexStart}
                                padding={10}
                            >
                                <Text
                                    material={descriptionTextMaterial}
                                    fontSize={24}
                                    textAlign="center"
                                >
                                    {insertNewlines(modeState.description, 44)}
                                </Text>
                            </Container>
                        </Container>
                    </Column>
                </Window>
                <Row justifyContent={Justify.SpaceAround}>
                    <MenuButton
                        width={250}
                        height={54}
                        margin={6}
                        color={colorSwatch.CancelButton}
                        fontSize={28}
                        icon={UiTexturesManager.instance.home}
                        iconWidth={38}
                        iconHeight={40}
                        sound={Sounds.cancel}
                        onClick={() => goToPreviousMenu(MenuPanels.ModeSelect)}
                    >
                        {Localization.instance.get('LoseProgressDialogReject')}
                    </MenuButton>
                    {isTextReadyToRender ? (
                        modeState.isImplemented && !modeState.isLocked ? (
                            <MenuButton
                                width={250}
                                height={54}
                                margin={6}
                                color={colorSwatch.AcceptButton}
                                fontSize={32}
                                iconWidth={38}
                                iconHeight={40}
                                onClick={handleClick}
                                icon={UiTexturesManager.instance.arrow}
                            >
                                {Localization.instance.get('Next')}
                            </MenuButton>
                        ) : (
                            <Text
                                width={250}
                                height={54}
                                top={18}
                                margin={6}
                                fontSize={24}
                                textAlign="center"
                                material={textMaterial}
                                text={modeState.isImplemented ? 'Locked' : 'Coming Soon'}
                            ></Text>
                        )
                    ) : null}
                </Row>
            </Column>
        </Container>
    );
};
